/**
 * 編集用のクラス名を生成する関数
 * @param {string} name - フィールド名（スネークケース）
 * @returns {string} 編集用クラス名
 */
export const getTargetClassFromName = (name) => {
  return `edit_${name}`;
};

/**
メッセージタイプの定義
*/
export const MESSAGE_TYPES = {
  TEXT_UPDATE: "TEXT_UPDATE", // 文字列更新
  IMAGE_UPDATE: "IMAGE_UPDATE", // 画像更新
  VIDEO_UPDATE: "VIDEO_UPDATE", // 動画更新
  ARRAY_ITEM_UPDATE: "ARRAY_ITEM_UPDATE", // 配列要素の更新
  SCROLL_TO: "SCROLL_TO", // スクロール命令
  SELECT_INSTRUCTOR: "SELECT_INSTRUCTOR", // インストラクター選択
  DELETE_INSTRUCTOR: "DELETE_INSTRUCTOR", // インストラクター削除
  ADD_INSTRUCTOR: "ADD_INSTRUCTOR", // インストラクター追加
  SELECT_CONCEPT: "SELECT_CONCEPT", // コンセプト説明選択
  DELETE_CONCEPT: "DELETE_CONCEPT", // コンセプト説明削除
  ADD_CONCEPT: "ADD_CONCEPT", // コンセプト説明追加
  ADD_PRICE_SECTION: "ADD_PRICE_SECTION", // 料金セクションの追加
  DELETE_PRICE_SECTION: "DELETE_PRICE_SECTION", // 料金セクションの削除
  ADD_PRICE_ITEM: "ADD_PRICE_ITEM", // 料金項目の追加
  DELETE_PRICE_ITEM: "DELETE_PRICE_ITEM", // 料金項目の削除
  SELECT_PRICE_SECTION: "SELECT_PRICE_SECTION", // 料金セクションの選択
  MULTIPLE_IMAGES_UPDATE: "MULTIPLE_IMAGES_UPDATE", // 複数画像更新
  UPDATE_TEXT: "UPDATE_TEXT", // 汎用テキスト更新
  UPDATE_IMAGE: "UPDATE_IMAGE", // 汎用画像更新
};

/**
 * 画像更新時にiframeにメッセージを送信する関数
 * @param {Object} imageData - 画像データ
 * @param {string} fieldName - フィールド名
 * @param {Object} options - オプション
 * @returns {void}
 */
export const handleImageUpdate = (imageData, fieldName, options) => {
  const {
    contentKey = "pageContent",
    componentKey,
    currentData,
    setData,
  } = options;

  // ステート更新
  setData((prev) => ({ ...prev, [fieldName]: imageData }));

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    const targetClass = `.${getTargetClassFromName(fieldName)}`;

    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.IMAGE_UPDATE,
        target: targetClass,
        contentKey,
        componentKey,
        fieldName,
        value: imageData,
        allData: { ...currentData, [fieldName]: imageData },
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * 動画更新時にiframeにメッセージを送信する関数
 * @param {Object} videoData - 動画データ
 * @param {string} fieldName - フィールド名
 * @param {Object} options - オプション
 * @returns {void}
 */
export const handleVideoUpdate = (videoData, fieldName, options) => {
  const { contentKey = "pageContent", currentData, setData } = options;

  // ステート更新
  setData((prev) => ({ ...prev, [fieldName]: videoData }));

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.VIDEO_UPDATE,
        contentKey,
        fieldName,
        value: videoData,
        allData: { ...currentData, [fieldName]: videoData },
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * 入力変更時にiframeにメッセージを送信する関数
 * @param {Event} e - イベントオブジェクト
 * @param {Object} options - オプション
 * @param {string} options.contentKey - コンテンツのキー名
 * @param {Object} options.currentData - 現在のデータ
 * @param {Function} options.setData - データ更新関数
 * @returns {void}
 */
export const handleInputChangeWithScroll = (e, options = {}) => {
  const { contentKey = "pageContent", setData } = options;
  if (typeof setData !== "function") {
    console.error("setData is not a function");
    return;
  }
  const { name, value } = e.target;
  const eventType = e.type;

  console.log("handleInputChangeWithScroll called with:", {
    name,
    value,
    eventType,
    contentKey,
  });

  setData((prev) => {
    const newData = { ...prev, [name]: value };
    const iframe = document.querySelector("iframe");

    console.log("iframe found:", !!iframe);
    console.log("iframe.contentWindow:", !!iframe?.contentWindow);

    if (iframe?.contentWindow) {
      const targetSelector = `.${getTargetClassFromName(name)}`;
      const messageType =
        eventType === "focus"
          ? MESSAGE_TYPES.SCROLL_TO
          : MESSAGE_TYPES.TEXT_UPDATE;

      const messageData = {
        type: messageType,
        target: targetSelector,
        contentKey,
        fieldName: name,
        value,
        // 個別フィールド更新時はallDataを送信しない
      };

      console.log("Sending postMessage:", messageData);
      console.log("To origin:", process.env.REACT_APP_ORIGIN);

      iframe.contentWindow.postMessage(
        messageData,
        process.env.REACT_APP_ORIGIN
      );
    } else {
      console.warn("iframe or iframe.contentWindow not available");
    }
    return newData;
  });
};

/**
 * 汎用的なiframe更新関数
 * @param {string} messageType - メッセージタイプ
 * @param {string} fieldName - フィールド名
 * @param {*} value - 値
 * @param {string} contentKey - コンテンツキー
 * @returns {void}
 */
export const sendUpdateToIframe = (
  messageType,
  fieldName,
  value,
  contentKey = "pageContent"
) => {
  const iframe = document.querySelector("iframe");
  console.log("sendUpdateToIframe called:", {
    messageType,
    fieldName,
    value,
    contentKey,
  });
  console.log("iframe found:", !!iframe);
  console.log("iframe.contentWindow:", !!iframe?.contentWindow);

  if (iframe?.contentWindow) {
    const targetClass = `.edit_${fieldName}`;
    const messageData = {
      type: messageType,
      target: targetClass,
      contentKey,
      fieldName,
      value,
    };

    console.log("Sending message to iframe:", messageData);
    console.log("To origin:", process.env.REACT_APP_ORIGIN);

    iframe.contentWindow.postMessage(messageData, process.env.REACT_APP_ORIGIN);
  } else {
    console.warn("iframe or iframe.contentWindow not available");
  }
};

/**
 * iframeにデータを送信する関数
 * @param {Object} data - 送信するデータ
 * @param {string} contentKey - コンテンツのキー名
 * @param {string} origin - 送信先オリジン
 * @returns {void}
 */
export const sendDataToIframe = (
  data,
  contentKey = "pageContent",
  origin,
  options = {}
) => {
  const iframe = document.querySelector("iframe");
  if (iframe?.contentWindow) {
    try {
      const message = {
        type: MESSAGE_TYPES.TEXT_UPDATE,
        contentKey,
        allData: data,
        isFullUpdate: true, // 全体更新フラグを追加
        isReset: options.isReset || false, // リセットフラグを追加
      };

      console.log("sendDataToIframe called:", {
        contentKey,
        hasAllData: !!data,
        studiosCount: data?.studios?.length,
        origin,
        isReset: message.isReset,
      });

      iframe.contentWindow.postMessage(message, origin);
    } catch (error) {
      console.error("iframeへのメッセージ送信中にエラーが発生しました:", error);
    }
  } else {
    console.warn("iframe or contentWindow not found");
  }
};

/**
 * 要素にスクロールするメッセージを送信する汎用関数
 * @param {string} fieldName - フィールド名
 * @param {*} value - 値（オプション）
 * @param {Object} options - オプション
 * @returns {void}
 */
export const sendScrollMessage = (fieldName, value = null, options = {}) => {
  const {
    contentKey = "pageContent",
    componentKey,
    messageType = MESSAGE_TYPES.SCROLL_TO,
    currentData = {},
  } = options;
  const iframe = document.querySelector("iframe");
  if (iframe?.contentWindow) {
    const targetClass = `.${getTargetClassFromName(fieldName)}`;
    iframe.contentWindow.postMessage(
      {
        type: messageType,
        target: targetClass,
        contentKey,
        componentKey,
        fieldName,
        value,
        allData: value ? { ...currentData, [fieldName]: value } : currentData,
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * 画像更新時にiframeにメッセージを送信し、対応する要素にスクロールする関数
 * @param {Object} imageData - 画像データ
 * @param {string} fieldName - フィールド名
 * @param {Object} options - オプション
 * @returns {void}
 */
export const handleImageUpdateWithScroll = (
  imageData,
  fieldName,
  options = {}
) => {
  const { contentKey = "pageContent", componentKey, setData } = options;
  if (typeof setData !== "function") {
    console.warn(
      "handleImageUpdateWithScroll: setData is not a function, skipping"
    );
    return;
  }
  setData((prev) => {
    const newData = { ...prev, [fieldName]: imageData };
    const iframe = document.querySelector("iframe");
    if (iframe?.contentWindow) {
      const targetClass = `.${getTargetClassFromName(fieldName)}`;
      iframe.contentWindow.postMessage(
        {
          type: MESSAGE_TYPES.IMAGE_UPDATE,
          target: targetClass,
          contentKey,
          componentKey,
          fieldName,
          value: imageData,
          allData: newData,
        },
        process.env.REACT_APP_ORIGIN
      );
    }
    return newData;
  });
};

/**
 * 動画更新時にiframeにメッセージを送信し、対応する要素にスクロールする関数
 * @param {Object} videoData - 動画データ
 * @param {string} fieldName - フィールド名
 * @param {Object} options - オプション
 * @returns {void}
 */
export const handleVideoUpdateWithScroll = (
  videoData,
  fieldName,
  options = {}
) => {
  const { contentKey = "pageContent", setData } = options;
  if (typeof setData !== "function") {
    console.error("setData is not a function");
    return;
  }
  setData((prev) => {
    const newData = { ...prev, [fieldName]: videoData };
    const iframe = document.querySelector("iframe");
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: MESSAGE_TYPES.VIDEO_UPDATE,
          contentKey,
          fieldName,
          value: videoData,
          allData: newData,
        },
        process.env.REACT_APP_ORIGIN
      );
    }
    return newData;
  });
};

/**
 * 配列要素更新時にiframeにメッセージを送信する関数
 * @param {string} arrayFieldName - 配列フィールド名（例: "instructors", "about_items"）
 * @param {number} index - 配列のインデックス
 * @param {string} itemFieldName - 要素内のフィールド名（例: "name", "profile"）
 * @param {*} value - 更新値
 * @param {Object} options - オプション
 * @returns {void}
 */
export const handleArrayItemUpdate = (
  arrayFieldName,
  index,
  itemFieldName,
  value,
  options = {}
) => {
  const {
    contentKey = "pageContent",
    currentData = {},
    setData,
    eventType = "change",
    genreIndex = null, // ジャンルのような入れ子配列用
  } = options;

  if (typeof setData !== "function") {
    console.error("setData is not a function");
    return;
  }

  setData((prev) => {
    const newData = {
      ...prev,
      [arrayFieldName]:
        prev[arrayFieldName]?.map((item, i) => {
          if (i === index) {
            // ジャンルのような入れ子配列の場合
            if (genreIndex !== null && Array.isArray(item[itemFieldName])) {
              const updatedArray = [...(item[itemFieldName] || [])];
              updatedArray[genreIndex] = value;
              return { ...item, [itemFieldName]: updatedArray };
            }
            // 通常のフィールド更新
            return { ...item, [itemFieldName]: value };
          }
          return item;
        }) || [],
    };

    // iframeにメッセージ送信
    const iframe = document.querySelector("iframe");
    if (iframe?.contentWindow) {
      // ターゲットクラス名の生成
      let targetClass;
      if (genreIndex !== null) {
        targetClass = `.edit_${arrayFieldName.slice(
          0,
          -1
        )}_${index}_${itemFieldName}_${genreIndex}`;
      } else {
        targetClass = `.edit_${arrayFieldName.slice(
          0,
          -1
        )}_${index}_${itemFieldName}`;
      }

      const messageType =
        eventType === "focus"
          ? MESSAGE_TYPES.SCROLL_TO
          : MESSAGE_TYPES.ARRAY_ITEM_UPDATE;

      const messageData = {
        type: messageType,
        target: targetClass,
        contentKey,
        arrayFieldName,
        index,
        itemFieldName,
        genreIndex,
        value,
        // 個別フィールド更新時はallDataを送信しない
      };

      console.log("handleArrayItemUpdate sending message:", messageData);

      iframe.contentWindow.postMessage(
        messageData,
        process.env.REACT_APP_ORIGIN
      );
    }

    return newData;
  });
};

/**
 * 複数のターゲット要素を同時に更新する関数
 * @param {*} value - 更新値
 * @param {string[]} targets - ターゲットクラス名の配列
 * @param {Object} options - オプション
 * @returns {void}
 */
export const handleMultipleTargetUpdate = (value, targets, options = {}) => {
  const {
    contentKey = "pageContent",
    messageType = MESSAGE_TYPES.ARRAY_ITEM_UPDATE,
    arrayFieldName,
    index,
    itemFieldName,
  } = options;

  const iframe = document.querySelector("iframe");
  if (iframe?.contentWindow) {
    targets.forEach((target) => {
      iframe.contentWindow.postMessage(
        {
          type: messageType,
          target,
          contentKey,
          arrayFieldName,
          index,
          itemFieldName,
          value,
        },
        process.env.REACT_APP_ORIGIN
      );
    });
  }
};

/**
 * 汎用的な入力変更処理関数（PriceEditPageと同様の動作）
 * @param {Event} e - イベントオブジェクト
 * @param {Object} options - オプション
 * @param {string} options.contentKey - コンテンツのキー名
 * @param {Function} options.setData - データ更新関数
 * @returns {void}
 */
export const handleSimpleInputChange = (e, options = {}) => {
  const { contentKey = "pageContent", setData } = options;
  if (typeof setData !== "function") {
    console.error("setData is not a function");
    return;
  }

  const { name, value } = e.target;
  const eventType = e.type;

  setData((prev) => {
    const newData = { ...prev, [name]: value };

    // iframeにメッセージ送信
    const iframe = document.querySelector("iframe");
    if (iframe?.contentWindow) {
      const targetClass = `.edit_${name}`;
      const messageType =
        eventType === "focus"
          ? MESSAGE_TYPES.SCROLL_TO
          : MESSAGE_TYPES.TEXT_UPDATE;

      iframe.contentWindow.postMessage(
        {
          type: messageType,
          target: targetClass,
          contentKey,
          fieldName: name,
          value,
          allData: newData,
        },
        process.env.REACT_APP_ORIGIN
      );
    }

    return newData;
  });
};
